"use client";

import Container from "~/componentsDS/Layout/Container";
import Heading from "~/componentsDS/Typography/Heading";
import { QuizCTA } from "~/components/features/quiz";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import Reveal from "~/componentsDS/Reveal";
import ComparisonOestraCard from "../components/ComparisonOestraCard";
import ComparisonOthersCard from "../components/ComparisonOthersCard";
import { cn } from "~/lib/utils";
import { useCallback, useId, useState } from "react";
import { CarouselApi, CarouselContent, CarouselItem } from "~/components/ui/carousel";
import { motion } from "framer-motion";
import Carousel from "~/componentsDS/Carousel";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";

const TAB_ITEMS = [
    { value: "oestra", label: "Oestra®" },
    { value: "other", label: "The Other Methods" },
];

const components = [
    {
        id: "oestra",
        component: <ComparisonOestraCard />,
    },
    {
        id: "other",
        component: <ComparisonOthersCard />,
    }
];

const ctaTitle = "See if Oestra® Is a better fit";

const ComparisonSection = () => {
    const instanceId = useId();
    const sendEvent = useTrackEvent();
    const [activeTab, setActiveTab] = useState("oestra");
    const [emblaApi, setEmblaApi] = useState<CarouselApi>();

    const handleSelect = useCallback((api: CarouselApi) => {
        if (!api) return;

        const index = api.selectedScrollSnap() % 2;
        setActiveTab(TAB_ITEMS[index]?.value ?? "oestra");

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.PDP_INTERACTION,
                widget: "hrt_comparison",
                meta_data: {
                    click_text: TAB_ITEMS[index]?.label,
                },
            },
        });
    }, [sendEvent]);

    const handleTabClick = useCallback((tabValue: string) => {
        setActiveTab(tabValue);

        if (!emblaApi) return;

        const targetIndex = TAB_ITEMS.findIndex(t => t.value === tabValue);
        const currentIndex = emblaApi.selectedScrollSnap() % 2;

        if (currentIndex === targetIndex) return;

        if (targetIndex > currentIndex) {
            emblaApi.scrollNext();
        } else {
            emblaApi.scrollPrev();
        }
    }, [emblaApi]);

    return (
        <section className="pb-16 md:pb-[50px]">
            <Container>
                <Reveal>
                    <Heading
                        as="h2"
                        size="sm"
                        sizes={{ md: "md" }}
                        className="md:text-center text-primary500 mb-8"
                    >
                        Stop managing your{" "}
                        <Heading
                            as="i"
                            font="featureDisplay"
                            weight="400"
                            className="text-[28px] md:text-[36px]"
                        >
                            hormones{" "}
                        </Heading>
                        like a to-do list
                    </Heading>
                </Reveal>

                <div className="md:hidden flex flex-col items-center -mx-6">
                    <div className="inline-flex rounded-full bg-gray200 p-2 mx-6">
                        <div className="relative grid grid-cols-2 gap-1">
                            {TAB_ITEMS.map((tab) => {
                                const isActive = activeTab === tab.value;

                                return (
                                    <button
                                        key={tab.value}
                                        type="button"
                                        onClick={() => handleTabClick(tab.value)}
                                        className={cn(
                                            "relative z-10 flex h-10 pt-[2px] min-w-[120px] items-center justify-center rounded-full px-3 font-suisseBP text-center text-[18px] leading-none transition-colors duration-200",
                                            isActive
                                                ? "text-primary500"
                                                : "text-gray400"
                                        )}
                                    >
                                        {isActive ? (
                                            <motion.div
                                                layoutId={`${instanceId}-active-pill`}
                                                className="absolute inset-0 rounded-full bg-white shadow-[0_1px_4px_rgba(25,34,61,0.12)]"
                                                transition={{
                                                    type: "spring",
                                                    stiffness: 400,
                                                    damping: 32,
                                                    mass: 0.8,
                                                }}
                                            />
                                        ) : null}

                                        <span className="relative z-10 whitespace-nowrap">
                                            {tab.label}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <Carousel
                        loop={true}
                        opts={{
                            align: "center",
                            skipSnaps: false,
                            dragFree: false
                        }}
                        dotCount={2}
                        setApi={setEmblaApi}
                        onSelect={handleSelect}
                        className="w-full mt-6"
                    >
                        <CarouselContent className="-ml-2">
                            {[...components, ...components].map(({id, component}, index) => (
                                <CarouselItem key={`${index}-${id}`} className="pl-2 basis-full">
                                    {component}
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                    </Carousel>
                </div>

                <div className="hidden md:flex gap-6">
                    <div className="w-full">
                        <ComparisonOestraCard />
                    </div>
                    <div className="w-[410px] shrink-0">
                        <ComparisonOthersCard />
                    </div>
                </div>

                <div className="hidden md:flex justify-center mt-8">
                    <QuizCTA
                        variant="primary"
                        eventData={{
                            event_name: GTM_EVENT_NAMES.PDP_INTERACTION,
                            widget: "hrt_comparison",
                            meta_data: {
                                click_text: ctaTitle,
                            },
                        }}
                        href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                        size="md"
                    >
                        {ctaTitle}
                    </QuizCTA>
                </div>
            </Container>
        </section>
    )
};

export default ComparisonSection;
